import React, { type PropsWithChildren, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Dimensions,
  type ViewStyle,
  Modal,
  TouchableOpacity,
  View,
  BackHandler,
  Platform,
  KeyboardAvoidingView,
  StyleSheet,
} from 'react-native';
 
const screenHeight = Dimensions.get('screen').height;
const ANIMATION_DURATION = 300;
 
export default function BottomSheet(
  props: PropsWithChildren<{
    visible: boolean;
    keyboardAvoidingView?: boolean;
    containerStyle?: ViewStyle;
    onDismiss: () => void;
  }>,
) {
  const { children, visible, containerStyle, onDismiss, keyboardAvoidingView = false } = props;
  const [modalVisible, setModalVisible] = useState(visible);
  const contentPosition = useRef(new Animated.Value(screenHeight)).current;
 
  const insets = {
    bottom: Platform.OS === 'ios' ? 30 : 10,
  };
 
  useEffect(() => {
    if (visible) {
      setModalVisible(true);
      Animated.timing(contentPosition, {
        toValue: 0,
        duration: ANIMATION_DURATION,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(contentPosition, {
        toValue: screenHeight,
        duration: ANIMATION_DURATION,
        useNativeDriver: true,
      }).start();
      setTimeout(() => {
        setModalVisible(false);
      }, ANIMATION_DURATION - 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);
 
  // registers back listener to close bottomsheet on hardback press
  useEffect(() => {
    const onHardBackPress = () => {
      if (visible) {
        onDismiss();
      }
      return visible; // returns true when bottomsheet is visible to prevent other back handler to invoke
    };
 
    const subscription = BackHandler.addEventListener('hardwareBackPress', onHardBackPress);
 
    return () => {
      try {
        subscription?.remove();
      } catch (error) {
        console.warn('Failed to remove BottomSheet BackHandler:', error);
      }
    };
  }, [onDismiss, visible]);
 
  const renderContent = () => {
    return (
      <Animated.View
        style={[styles.contentContainer, { transform: [{ translateY: contentPosition }] }]}
      >
        <View
          style={[
            styles.safeareaWrapper,
            {
              paddingBottom: insets.bottom + (Platform.OS === 'ios' ? 0 : 8),
            },
            containerStyle || {},
          ]}
        >
          {children}
        </View>
      </Animated.View>
    );
  };
 
  if (!modalVisible) {
    return null;
  }
 
  return (
    <Modal transparent visible={modalVisible} animationType="fade" onRequestClose={onDismiss}>
      <View style={styles.modalContainer}>
        <TouchableOpacity
          activeOpacity={1}
          style={styles.overlay}
          onPress={(event) => {
            event.target === event.currentTarget && onDismiss();
          }}
        />
        {keyboardAvoidingView ? (
          <KeyboardAvoidingView enabled behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
            {renderContent()}
          </KeyboardAvoidingView>
        ) : (
          renderContent()
        )}
      </View>
    </Modal>
  );
}
 
const styles = StyleSheet.create({
  modalContainer: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)' },
  contentContainer: {
    minHeight: 0,
    maxHeight: '80%',
    borderTopRightRadius: 16,
    borderTopLeftRadius: 16,
  },
  safeareaWrapper: {
    backgroundColor: 'white',
    borderTopRightRadius: 16,
    borderTopLeftRadius: 16,
  },
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
});
